import { MatterPhysicsUpdater } from '../core/modules/MatterPhysicsUpdater';
import { ParallaxController } from '../core/modules/Parallax/ParallaxController';
import { LevelManager } from './LevelManager';
import { Level } from './levels/level';
import { LevelUtil } from './levels/LevelUtil';
import { ThemeUtil } from './themes/ThemeUtil';
import { RunSettings, RunSettingsHandler } from './RunSettingsHandler';
import { TrafficManager } from './TrafficManager';
import { VehicleFactory } from './VehicleFactory';
import { VehicleManager } from './VehicleManager';
import { Theme, Weather } from './themes/theme';

export interface RunManagerOptions {
  cameraOffset?: number;
  cameraBoundsX?: number;
  cameraBoundsY?: number;
  cameraBoundsWidth?: number;
  cameraBoundsHeight?: number;
}

export class RunManager {
  public static events = {
    PlayerDeath: 'playerDeath',
    WorldSwitchStarted: 'worldSwitchStarted',
    WorldSwitchEnded: 'worldSwitchEnded',
    RunStarted: 'runStarted',
    RunOver: 'runOver'
  };

  protected scene: Phaser.Scene;
  protected isInitialised: boolean;
  protected levelManager: LevelManager;
  protected parallaxController: ParallaxController;
  protected matterUpdater: MatterPhysicsUpdater;
  protected camera: Phaser.Cameras.Scene2D.Camera;
  protected vehicleLayer: Phaser.GameObjects.Layer;
  protected gameUILayer: Phaser.GameObjects.Layer;
  protected vehicleManager: VehicleManager;
  protected vehicleFactory: VehicleFactory;
  protected trafficManager: TrafficManager;
  protected options: Required<RunManagerOptions>;

  protected runSettingsHandler: RunSettingsHandler | undefined;
  protected level: Level | undefined;
  protected theme: Theme | undefined;

  protected instantRestart: boolean;
  protected regenOnRestart: boolean;

  protected hasStartedRun: boolean;
  protected isRunning: boolean;
  protected startLineX: number;
  protected endLineX: number;
  // protected distanceTravelled: number;

  /** An amount in pixels of chunk distance to spawn initially. */
  protected initialDistanceToGenerate: number;
  protected shiftDistance: number;

  constructor(
    scene: Phaser.Scene,
    levelManager: LevelManager,
    parallaxController: ParallaxController,
    matterUpdater: MatterPhysicsUpdater,
    camera: Phaser.Cameras.Scene2D.Camera,
    vehicleLayer: Phaser.GameObjects.Layer,
    gameUILayer: Phaser.GameObjects.Layer,
    options: RunManagerOptions = {}
  ) {
    this.scene = scene;
    this.levelManager = levelManager;
    this.parallaxController = parallaxController;
    this.matterUpdater = matterUpdater;
    this.camera = camera;
    this.vehicleLayer = vehicleLayer;
    this.gameUILayer = gameUILayer;

    this.vehicleManager = new VehicleManager(this.scene, this.levelManager);
    this.vehicleFactory = new VehicleFactory(this.scene, this.levelManager, this.vehicleManager, this.matterUpdater, this.vehicleLayer, this.gameUILayer);
    this.trafficManager = new TrafficManager(this.scene, this.vehicleFactory, this.levelManager, this.matterUpdater, this.vehicleLayer, this.gameUILayer);

    this.options = Object.assign(
      {},
      { cameraOffset: -400, cameraBoundsX: -1000, cameraBoundsY: -384, cameraBoundsWidth: 1000, cameraBoundsHeight: 0 },
      options
    );

    this.instantRestart = true;
    this.regenOnRestart = true;
    this.isInitialised = false;

    this.hasStartedRun = false;
    this.isRunning = false;
    this.startLineX = 0;
    this.endLineX = 0;

    this.initialDistanceToGenerate = 3000;
    this.shiftDistance = 100000;

    this.scene.events.on(Phaser.Scenes.Events.UPDATE, this.update, this);
    // this.scene.events.on(RunManager.events.PlayerDeath, this.onPlayerDeath, this);
    this.scene.events.once(Phaser.Scenes.Events.SHUTDOWN, this.shutdown, this);
  }

  get Level() {
    return this.level;
  }

  get Theme() {
    return this.theme;
  }

  get TrafficManager() {
    return this.trafficManager;
  }

  public initRun(runSettings: RunSettings, level: Level, theme: Theme) {
    this.runSettingsHandler = new RunSettingsHandler(this.scene, runSettings);
    this.level = level;
    this.theme = theme;

    this.instantRestart = runSettings.instantRestart;
    this.regenOnRestart = runSettings.regenOnRestart;

    this.setupWorld(this.level);

    this.resetRun();
  }

  public resetRun() {
    this.hasStartedRun = false;
    this.isRunning = false;

    // let spawnPointX = this.level?.playerSpawnPoint.x ?? 0;
    // let spawnPointY = this.level?.playerSpawnPoint.y ?? 0;

    this.startLineX = this.theme?.roadDetails.startPointX ?? 0;
    this.endLineX = this.levelManager.LevelEndPointX ?? 0;
    console.log('RESETENDPOINT ', this.endLineX);
    this.camera?.setBounds(
      this.options.cameraBoundsX,
      this.options.cameraBoundsY,
      (this.levelManager.LevelEndPointX ?? 0) + this.options.cameraBoundsWidth,
      this.options.cameraBoundsHeight
    );

    // this.player?.resetPosition(spawnPointX, spawnPointY);
  }

  public startRun() {
    this.hasStartedRun = true;
    this.isRunning = true;

    this.trafficManager.start();
    this.vehicleManager.start();

    this.scene.events.emit(RunManager.events.RunStarted);
  }

  public pauseRun() {
    this.isRunning = false;

    this.trafficManager.pause();
    this.vehicleManager.pause();
  }

  public resumeRun() {
    this.isRunning = true;

    this.trafficManager.resume();
    this.vehicleManager.resume();
  }

  public endRun() {
    this.hasStartedRun = false;
    this.isRunning = false;

    this.trafficManager.end();
    this.vehicleManager.end();

    if (this.instantRestart && this.vehicleManager.PlayerTaxi && this.level && this.theme) {
      if (this.regenOnRestart) {
        // this.levelManager?.endLevel();
        this.levelManager.setupLevel(this.theme.roadDetails.startPointX, this.theme.roadDetails.startPointY, this.initialDistanceToGenerate);
        // this.playerTaxi?.respawn(this.level?.playerSpawnPoint.x ?? 0, this.level?.playerSpawnPoint.y ?? 0);
        // this.camera?.startFollow(this.playerTaxi, true, 0.9, 0.5, -400, 0);
        this.scene.time.delayedCall(1000, () => this.startRun());
      } else {
        // this.levelManager?.resetLevel();
        // this.playerTaxi?.respawn(this.level?.playerSpawnPoint.x ?? 0, this.level?.playerSpawnPoint.y ?? 0);
        // this.camera?.startFollow(this.playerTaxi, true, 0.9, 0.5, -400, 0);
        this.scene.time.delayedCall(1000, () => this.startRun());
      }
    } else {
      this.levelManager?.endLevel();

      // Show endscreen.
      this.scene.events.emit(RunManager.events.RunOver);
    }
  }

  protected update(time: number, delta: number) {
    if (!this.hasStartedRun || !this.isRunning) return;

    this.trafficManager.updateTraffic(time, delta);

    this.levelManager.checkOnPassengers(this.camera.worldView.right);
    this.levelManager.spawnEntitiesAhead(time, delta, this.camera.worldView.right);
    this.levelManager.despawnEntitiesBehindOrUsed(time, delta, this.camera.worldView.left);

    // if (this.player && this.player.x >= this.shiftDistance) {
    //   this.shiftRunToZero();
    // }

    // Check end conditions.
    if (this.levelManager.HasDroppedAllPassengers) {
      this.endRun();
    } else if (
      this.vehicleManager.PlayerTaxi &&
      this.levelManager.HasSpawnedAllDropOffs &&
      this.vehicleManager.PlayerTaxi.x > this.levelManager.FurthestXDropOff
    ) {
      this.endRun();
    } else if (this.vehicleManager.PlayerTaxi && this.vehicleManager.PlayerTaxi.x >= this.endLineX) {
      this.endRun();
    }
  }

  protected shutdown() {
    this.scene.events.off(Phaser.Scenes.Events.UPDATE, this.update, this);
    // this.scene.events.off(RunManager.events.PlayerDeath, this.onPlayerDeath, this);
    // this.isInitialised = false;
  }

  protected setupWorld(newLevel: Level) {
    this.level = newLevel;
    this.theme = ThemeUtil.getTheme(this.level.theme);

    this.parallaxController?.clearLayers();
    this.parallaxController?.buildLayersFromData(
      this.theme.parallaxLayers.map((data) => {
        return { ...data };
      })
    );

    this.levelManager.changeLevel(this.level, this.theme);
    this.levelManager.setupLevel(this.theme.roadDetails.startPointX, this.theme.roadDetails.startPointY, this.initialDistanceToGenerate);

    // this.player.resetPosition(this.world.playerSpawnPoint.x, this.world.playerSpawnPoint.y);

    // Spawn all vehicles.
    this.trafficManager.init(this.level, this.theme);
    this.vehicleManager.init(this.vehicleFactory, this.level);

    if (this.vehicleManager.PlayerTaxi && this.camera) {
      // Moving the camera instantly to avoid jarring lerp jump.
      this.camera.setScroll(this.vehicleManager.PlayerTaxi.x - this.options.cameraOffset, this.vehicleManager.PlayerTaxi.y);
      this.camera.startFollow(this.vehicleManager.PlayerTaxi, true, 0.9, 0, this.options.cameraOffset, 0);

      this.updateFinishLine();
    }
  }

  // protected shiftRunToZero() {
  //   if (!this.levelManager) return;

  //   console.log('%cShifting to zero!!!', 'background: #000; color: #00ffff');
  //   this.parallaxController?.freezePosition();

  //   // TODO: Rather than move the start of the chunk to 0 offset the the chunk position by how far the player is into the chunk.
  //   // Find chunk player in. (player.x between chunkLeftX and chunkRightX).
  //   // offset = player.x - chunkLeftX.
  //   let offset = this.levelManager.shiftLevel(0);

  //   this.playerTaxi?.shiftPosition(this.playerTaxi.x - offset, this.playerTaxi.y);
  //   this.camera?.setScroll(this.camera.scrollX - offset, this.camera.scrollY);
  //   this.updateFinishLine();

  //   this.parallaxController?.unfreezePosition();
  // }

  protected updateFinishLine() {
    // this.endLineX = this.levelManager?.LevelEndX ?? 0;
    // this.camera?.setBounds(-1000, -384, (this.levelManager?.LevelEndPointX ?? 0) + 1000, 768);
  }
}
